import { useRef, useState } from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import api, { formatApiErrorDetail } from "@/lib/api";
import { todayISO } from "@/lib/localDate";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  UploadSimpleIcon, DownloadSimpleIcon, CheckCircleIcon, WarningCircleIcon, XIcon, FileXlsIcon, EyeIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const P_STATUSES = ["initiation", "planning", "execution", "closed", "hold"];
const P_PRIORITIES = ["high", "medium", "low"];
const T_STATUSES = ["open", "wip", "test", "uat", "cutover", "live", "closed", "cancelled", "hold"];
const T_PRIORITIES = ["low", "medium", "high", "urgent"];
const ROW_TYPES = ["Project", "Task", "Subtask"];
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const TEMPLATE_COLUMNS = [
  "Type", "Project", "Parent Task", "Title", "Initiative", "Entity", "Description",
  "Risk / Challenges", "Plan", "Task Number", "Status", "Priority", "Tech Team", "Func Team",
  "Project Manager Email", "Assignee Email", "Start Date", "ETA", "Revised ETA",
  "Requested By", "FY Lic Cost $", "Imp Cost $", "Comments",
];

function downloadTemplate() {
  const csv = Papa.unparse({ fields: TEMPLATE_COLUMNS, data: [] });
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "project_task_subtask_bulk_upload_template.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function parseMoney(raw) {
  if (raw == null || String(raw).trim() === "") return { value: null, ok: true };
  const cleaned = String(raw).replace(/[$,\s]/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? { value: n, ok: true } : { value: null, ok: false };
}

function excelRowToStrings(row) {
  const out = {};
  Object.keys(row).forEach((k) => {
    const v = row[k];
    if (v instanceof Date) {
      const y = v.getFullYear();
      const m = String(v.getMonth() + 1).padStart(2, "0");
      const d = String(v.getDate()).padStart(2, "0");
      out[k] = `${y}-${m}-${d}`;
    } else {
      out[k] = v;
    }
  });
  return out;
}

function checkDate(errors, label, raw) {
  const v = String(raw || "").trim();
  if (!v) return "";
  if (!ISO_DATE.test(v)) { errors.push(`${label} must be in YYYY-MM-DD format`); return ""; }
  return v;
}

function validateProjectRow(raw, { initiatives, managers }) {
  const errors = [];
  const title = String(raw["Title"] || "").trim();
  if (!title) errors.push("Title is required");

  const initiativeName = String(raw["Initiative"] || "").trim();
  let initiative_id = "";
  if (!initiativeName) {
    errors.push("Initiative is required");
  } else {
    const match = initiatives.find((i) => i.title.trim().toLowerCase() === initiativeName.toLowerCase());
    if (!match) errors.push(`No initiative named "${initiativeName}"`);
    else initiative_id = match._id;
  }

  const managerEmail = String(raw["Project Manager Email"] || "").trim();
  let owner_id = "";
  if (!managerEmail) {
    errors.push("Project Manager Email is required");
  } else {
    const match = managers.find((u) => (u.email || "").trim().toLowerCase() === managerEmail.toLowerCase());
    if (!match) errors.push(`No manager/owner found with email "${managerEmail}"`);
    else owner_id = match._id;
  }

  const statusRaw = String(raw["Status"] || "").trim().toLowerCase();
  let status = "initiation";
  if (statusRaw) {
    if (!P_STATUSES.includes(statusRaw)) errors.push(`Status must be one of: ${P_STATUSES.join(", ")}`);
    else status = statusRaw;
  }

  const priorityRaw = String(raw["Priority"] || "").trim().toLowerCase();
  let priority = "medium";
  if (priorityRaw) {
    if (!P_PRIORITIES.includes(priorityRaw)) errors.push(`Priority must be one of: ${P_PRIORITIES.join(", ")}`);
    else priority = priorityRaw;
  }

  const start_date = checkDate(errors, "Start Date", raw["Start Date"]) || todayISO();
  const end_date = checkDate(errors, "ETA", raw["ETA"]);
  const revised_eta = checkDate(errors, "Revised ETA", raw["Revised ETA"]);

  const fyLic = parseMoney(raw["FY Lic Cost $"]);
  if (!fyLic.ok) errors.push("FY Lic Cost $ must be a number");
  const impCost = parseMoney(raw["Imp Cost $"]);
  if (!impCost.ok) errors.push("Imp Cost $ must be a number");

  return {
    errors,
    kind: "project",
    title,
    data: {
      title, initiative_id, entity: String(raw["Entity"] || "").trim(),
      description: String(raw["Description"] || "").trim(),
      risk_challenges: String(raw["Risk / Challenges"] || "").trim(),
      plan: String(raw["Plan"] || "").trim(),
      task_number: String(raw["Task Number"] || "").trim(),
      status, priority,
      tech_team: String(raw["Tech Team"] || "").trim(),
      func_team: String(raw["Func Team"] || "").trim(),
      owner_id, start_date, end_date, revised_eta,
      fy_lic_cost: fyLic.value, imp_cost: impCost.value,
      comments: String(raw["Comments"] || "").trim(),
    },
  };
}

function validateTaskLikeRow(raw, { isSubtask, projectTitleSet, parentTaskSet, users }) {
  const errors = [];
  const title = String(raw["Title"] || "").trim();
  if (!title) errors.push("Title is required");

  const projectName = String(raw["Project"] || "").trim();
  if (!projectName) {
    errors.push("Project is required");
  } else if (!projectTitleSet.has(projectName.toLowerCase())) {
    errors.push(`No project named "${projectName}" (not found among existing projects or Project rows in this file)`);
  }

  let parentTaskName = "";
  if (isSubtask) {
    parentTaskName = String(raw["Parent Task"] || "").trim();
    if (!parentTaskName) {
      errors.push("Parent Task is required for a Subtask row");
    } else if (projectName && !parentTaskSet.has(`${projectName.toLowerCase()}|${parentTaskName.toLowerCase()}`)) {
      errors.push(`No task named "${parentTaskName}" under project "${projectName}" (existing or in this file)`);
    }
  }

  const statusRaw = String(raw["Status"] || "").trim().toLowerCase();
  let status = "open";
  if (statusRaw) {
    if (!T_STATUSES.includes(statusRaw)) errors.push(`Status must be one of: ${T_STATUSES.join(", ")}`);
    else status = statusRaw;
  }

  const priorityRaw = String(raw["Priority"] || "").trim().toLowerCase();
  let priority = "medium";
  if (priorityRaw) {
    if (!T_PRIORITIES.includes(priorityRaw)) errors.push(`Priority must be one of: ${T_PRIORITIES.join(", ")}`);
    else priority = priorityRaw;
  }

  const assigneeRaw = String(raw["Assignee Email"] || "").trim();
  const assignee_ids = [];
  if (!assigneeRaw) {
    errors.push("Assignee Email is required (at least one)");
  } else {
    const emails = assigneeRaw.split(/[;,]/).map((e) => e.trim()).filter(Boolean);
    for (const email of emails) {
      const match = users.find((u) => (u.email || "").trim().toLowerCase() === email.toLowerCase());
      if (!match) errors.push(`No user found with email "${email}"`);
      else assignee_ids.push(match._id);
    }
  }

  const eta = checkDate(errors, "ETA", raw["ETA"]);
  if (!eta) errors.push("ETA is required");
  const revised_eta = checkDate(errors, "Revised ETA", raw["Revised ETA"]);

  const requested_by = String(raw["Requested By"] || "").trim();
  if (!requested_by) errors.push("Requested By is required");

  return {
    errors,
    kind: isSubtask ? "subtask" : "task",
    title,
    projectName,
    parentTaskName,
    data: {
      title, status, priority, assignee_ids,
      description: String(raw["Description"] || "").trim(),
      eta: eta || null,
      revised_eta: revised_eta || null,
      requested_by,
    },
  };
}

function classifyAndValidate(parsedRaws, { initiatives, managers, users, projectTitleSet, parentTaskSet }) {
  return parsedRaws.map((raw) => {
    const typeRaw = String(raw["Type"] || "").trim().toLowerCase();
    const type = ROW_TYPES.find((t) => t.toLowerCase() === typeRaw);
    if (!type) {
      return { raw, errors: [`Type must be one of: ${ROW_TYPES.join(", ")}`], kind: null, title: raw["Title"] || "" };
    }
    if (type === "Project") {
      return { raw, ...validateProjectRow(raw, { initiatives, managers }) };
    }
    return {
      raw,
      ...validateTaskLikeRow(raw, { isSubtask: type === "Subtask", projectTitleSet, parentTaskSet, users }),
    };
  });
}

const REQUIRED_COLUMNS = {
  Project: ["Type", "Title", "Initiative", "Project Manager Email"],
  Task: ["Type", "Project", "Title", "Assignee Email", "ETA", "Requested By"],
  Subtask: ["Type", "Project", "Parent Task", "Title", "Assignee Email", "ETA", "Requested By"],
};

const PREVIEW_ROWS = [
  {
    Type: "Project", Title: "Ammonia Tank Revamp", Initiative: "IPL FOCUS AREAS",
    Status: "initiation", Priority: "medium", "Project Manager Email": "firozuddin.ahmed@indorama.com",
  },
  {
    Type: "Task", Project: "Ammonia Tank Revamp", Title: "Replace pressure relief valve on PV-605",
    Status: "open", Priority: "high", "Assignee Email": "amit.hazra@indorama.com",
    ETA: "2026-12-01", "Requested By": "Deepu Sivadas",
  },
  {
    Type: "Subtask", Project: "Ammonia Tank Revamp", "Parent Task": "Replace pressure relief valve on PV-605",
    Title: "Procure replacement valve from UOP", Status: "open", Priority: "medium",
    "Assignee Email": "amit.hazra@indorama.com", ETA: "2026-11-15", "Requested By": "Deepu Sivadas",
  },
];

const KIND_CHIP = {
  project: "bg-indigo-50 text-indigo-700 border-indigo-200",
  task: "bg-sky-50 text-sky-700 border-sky-200",
  subtask: "bg-teal-50 text-teal-700 border-teal-200",
};

const KIND_DOT = { Project: "#4F46E5", Task: "#0176D3", Subtask: "#0F9488" };

function columnLetter(index) {
  let n = index + 1;
  let s = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    s = String.fromCharCode(65 + rem) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

function PreviewSheet() {
  return (
    <div className="rounded-sm border border-zinc-200 min-w-0">
      <div className="px-4 py-2.5 border-b border-zinc-200 bg-zinc-50">
        <div className="text-sm font-semibold text-zinc-800">Preview</div>
        <p className="text-xs text-zinc-500 mt-0.5">
          One row of each type, shown as it lines up in the spreadsheet. <b className="font-semibold text-zinc-700">Bold</b> cells
          with <b className="font-semibold text-zinc-700">*</b> are required for that row's Type; the rest can stay blank.
        </p>
        <p className="inline-flex items-center gap-1 px-2 py-1 mt-2 text-xs font-medium border rounded-sm text-amber-700 bg-amber-50 border-amber-200">
          Sample data only — for reference, not actual records.
        </p>
      </div>

      <div className="max-h-56 overflow-auto">
        <table className="border-collapse text-xs" style={{ minWidth: "max-content" }}>
          <thead>
            <tr>
              <th className="bg-zinc-100 border border-zinc-200 w-9 min-w-9" />
              {TEMPLATE_COLUMNS.map((_, i) => (
                <th key={i} className="bg-zinc-100 border border-zinc-200 text-zinc-400 font-mono font-normal px-2 py-1 text-center min-w-[140px]">
                  {columnLetter(i)}
                </th>
              ))}
            </tr>
            <tr>
              <th className="bg-zinc-100 border border-zinc-200 text-center text-zinc-400 font-mono">1</th>
              {TEMPLATE_COLUMNS.map((col) => (
                <th key={col} className="bg-zinc-100 border border-zinc-200 px-2 py-1.5 text-left font-semibold text-zinc-600 whitespace-nowrap">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PREVIEW_ROWS.map((row, idx) => {
              const kind = row.Type;
              const required = REQUIRED_COLUMNS[kind] || [];
              return (
                <tr key={kind}>
                  <td className="bg-zinc-50 border border-zinc-200 text-center text-zinc-400 font-mono">{idx + 2}</td>
                  {TEMPLATE_COLUMNS.map((col) => {
                    const value = row[col];
                    const isRequired = required.includes(col);
                    return (
                      <td
                        key={col}
                        className={`border border-zinc-200 px-2 py-1.5 whitespace-nowrap ${
                          isRequired ? "font-semibold text-zinc-900" : "text-zinc-400"
                        }`}
                      >
                        {col === "Type" && (
                          <span
                            className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle"
                            style={{ background: KIND_DOT[value] }}
                          />
                        )}
                        {value || "—"}
                        {isRequired && <span className="text-red-500">*</span>}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function BulkUploadProjectsModal({
  open, onOpenChange, initiatives, managers, projects, existingTasks, users, onDone,
}) {
  const fileInputRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const reset = () => {
    setFileName(""); setRows([]); setResult(null); setPreviewOpen(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = (v) => { if (!v) reset(); onOpenChange(v); };

  const processRows = (parsedRaws) => {
    const projectTitleSet = new Set(projects.map((p) => p.title.trim().toLowerCase()));
    parsedRaws.forEach((raw) => {
      if (String(raw["Type"] || "").trim().toLowerCase() === "project") {
        const t = String(raw["Title"] || "").trim();
        if (t) projectTitleSet.add(t.toLowerCase());
      }
    });

    const parentTaskSet = new Set(
      existingTasks.map((t) => {
        const proj = projects.find((p) => p._id === t.project_id);
        return proj ? `${proj.title.trim().toLowerCase()}|${(t.title || "").trim().toLowerCase()}` : null;
      }).filter(Boolean)
    );
    parsedRaws.forEach((raw) => {
      if (String(raw["Type"] || "").trim().toLowerCase() === "task") {
        const proj = String(raw["Project"] || "").trim();
        const t = String(raw["Title"] || "").trim();
        if (proj && t) parentTaskSet.add(`${proj.toLowerCase()}|${t.toLowerCase()}`);
      }
    });

    const parsed = classifyAndValidate(parsedRaws, { initiatives, managers, users, projectTitleSet, parentTaskSet });
    setRows(parsed);
    if (parsed.length === 0) toast.error("No rows found in that file");
  };

  const handleFile = (file) => {
    if (!file) return;
    setResult(null);
    setFileName(file.name);

    const isExcel = /\.xlsx$|\.xls$/i.test(file.name);
    if (isExcel) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const wb = XLSX.read(e.target.result, { type: "array", cellDates: true });
          const sheet = wb.Sheets[wb.SheetNames[0]];
          const parsedRaws = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: true }).map(excelRowToStrings);
          processRows(parsedRaws);
        } catch {
          toast.error("Could not read that file — make sure it's a valid .csv or Excel file");
        }
      };
      reader.onerror = () => toast.error("Could not read that file — make sure it's a valid .csv or Excel file");
      reader.readAsArrayBuffer(file);
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (res) => processRows(res.data || []),
      error: () => toast.error("Could not read that file — make sure it's a .csv file"),
    });
  };

  const validRows = rows.filter((r) => r.errors.length === 0);
  const invalidCount = rows.length - validRows.length;

  const submit = async () => {
    if (validRows.length === 0) return;
    setUploading(true);
    let created = 0;
    const failed = [];
    const projectIdByTitle = new Map(projects.map((p) => [p.title.trim().toLowerCase(), p._id]));
    const taskIdByKey = new Map(
      existingTasks.map((t) => {
        const proj = projects.find((p) => p._id === t.project_id);
        return proj ? [`${proj.title.trim().toLowerCase()}|${(t.title || "").trim().toLowerCase()}`, t._id] : null;
      }).filter(Boolean)
    );

    const projectRows = validRows.filter((r) => r.kind === "project");
    const taskRows = validRows.filter((r) => r.kind === "task");
    const subtaskRows = validRows.filter((r) => r.kind === "subtask");

    for (const row of projectRows) {
      try {
        const { data: proj } = await api.post("/projects", {
          ...row.data,
          start_date: row.data.start_date || null,
          end_date: row.data.end_date || null,
          revised_eta: row.data.revised_eta || null,
        });
        projectIdByTitle.set(row.data.title.trim().toLowerCase(), proj._id);
        created += 1;
      } catch (e) {
        failed.push({ title: row.data.title, kind: "Project", reason: formatApiErrorDetail(e?.response?.data?.detail) });
      }
    }

    for (const row of taskRows) {
      const project_id = projectIdByTitle.get(row.projectName.trim().toLowerCase());
      if (!project_id) {
        failed.push({ title: row.title, kind: "Task", reason: `Project "${row.projectName}" was not created — see its own error above` });
        continue;
      }
      try {
        const [primary, ...rest] = row.data.assignee_ids;
        const { data: created_task } = await api.post("/tasks", {
          title: row.data.title, description: row.data.description, status: row.data.status,
          priority: row.data.priority, project_id, assignee_id: primary || null,
          requested_by: row.data.requested_by, attachment_url: null,
          eta: row.data.eta, revised_eta: row.data.revised_eta,
        });
        for (const uid of rest) {
          await api.post(`/tasks/${created_task._id}/assignees`, { user_id: uid });
        }
        taskIdByKey.set(`${row.projectName.trim().toLowerCase()}|${row.title.trim().toLowerCase()}`, created_task._id);
        created += 1;
      } catch (e) {
        failed.push({ title: row.title, kind: "Task", reason: formatApiErrorDetail(e?.response?.data?.detail) });
      }
    }

    for (const row of subtaskRows) {
      const project_id = projectIdByTitle.get(row.projectName.trim().toLowerCase());
      const parent_task_id = taskIdByKey.get(`${row.projectName.trim().toLowerCase()}|${row.parentTaskName.trim().toLowerCase()}`);
      if (!project_id || !parent_task_id) {
        failed.push({ title: row.title, kind: "Subtask", reason: `Parent task "${row.parentTaskName}" under "${row.projectName}" was not created — see its own error above` });
        continue;
      }
      try {
        const [primary, ...rest] = row.data.assignee_ids;
        const { data: created_sub } = await api.post("/tasks", {
          title: row.data.title, description: row.data.description, status: row.data.status,
          priority: row.data.priority, project_id, parent_task_id, assignee_id: primary || null,
          requested_by: row.data.requested_by, attachment_url: null,
          eta: row.data.eta, revised_eta: row.data.revised_eta,
        });
        for (const uid of rest) {
          await api.post(`/tasks/${created_sub._id}/assignees`, { user_id: uid });
        }
        created += 1;
      } catch (e) {
        failed.push({ title: row.title, kind: "Subtask", reason: formatApiErrorDetail(e?.response?.data?.detail) });
      }
    }

    setUploading(false);
    setResult({ created, failed });
    if (created > 0) onDone();
    if (failed.length === 0) {
      toast.success(`${created} row${created === 1 ? "" : "s"} created`);
    } else {
      toast.error(`${created} created, ${failed.length} failed — see details below`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="rounded-sm max-w-3xl max-h-[85vh] overflow-y-auto min-w-0">
        <DialogHeader><DialogTitle className="font-display">Bulk Upload Projects, Tasks &amp; Subtasks</DialogTitle></DialogHeader>

        <div className="space-y-5 min-w-0">
          <div className="flex items-center justify-between gap-4 rounded-sm border border-[#CFE6FA] bg-[#EAF4FD] px-4 py-3">
            <div>
              <div className="text-sm font-semibold">Step 1 — Download the template</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button variant="outline" className="rounded-sm" onClick={() => setPreviewOpen((v) => !v)} data-testid="preview-bulk-example">
                <EyeIcon size={14} className="mr-1.5" /> Preview
              </Button>
              <Button variant="outline" className="rounded-sm" onClick={downloadTemplate} data-testid="download-bulk-template">
                <DownloadSimpleIcon size={14} className="mr-1.5" /> Download Template
              </Button>
            </div>
          </div>

          {previewOpen && <PreviewSheet />}

          <div>
            <div className="trk-uppercase-label mb-2">Step 2 — Upload your filled file</div>
            {!fileName ? (
              <label className="flex flex-col items-center justify-center gap-2 rounded-sm border border-dashed border-zinc-300 px-4 py-8 text-center cursor-pointer hover:bg-zinc-50">
                <UploadSimpleIcon size={22} className="text-zinc-400" />
                <div className="text-sm font-medium">Click to choose a .csv or Excel file</div>
                <div className="text-xs text-zinc-400">or drag it here</div>
                <input
                  ref={fileInputRef} type="file"
                  accept=".csv,text/csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                  className="hidden"
                  data-testid="bulk-upload-file-input"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
              </label>
            ) : (
              <div className="flex items-center gap-3 rounded-sm border border-zinc-200 bg-zinc-50 px-3 py-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-emerald-100 text-emerald-700">
                  <FileXlsIcon size={16} weight="bold" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{fileName}</div>
                  <div className="text-xs text-zinc-500">{rows.length} row{rows.length === 1 ? "" : "s"} detected</div>
                </div>
                <button className="text-xs text-zinc-500 hover:text-zinc-800" onClick={reset} data-testid="bulk-upload-remove-file">
                  <XIcon size={13} className="inline mr-1" />Remove
                </button>
              </div>
            )}
          </div>

          {rows.length > 0 && !result && (
            <div>
              <div className="trk-uppercase-label mb-2">Step 3 — Review before uploading</div>
              <div className="mb-2 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                  <CheckCircleIcon size={13} weight="fill" /> {validRows.length} row{validRows.length === 1 ? "" : "s"} ready
                </span>
                {invalidCount > 0 && (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700">
                    <WarningCircleIcon size={13} weight="fill" /> {invalidCount} row{invalidCount === 1 ? "" : "s"} need{invalidCount === 1 ? "s" : ""} fixing
                  </span>
                )}
              </div>
              <div className="max-h-64 overflow-auto rounded-sm border border-zinc-200">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-zinc-50 text-[10px] uppercase tracking-wide text-zinc-400">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">Row</th>
                      <th className="px-3 py-2 text-left font-semibold">Type</th>
                      <th className="px-3 py-2 text-left font-semibold">Title</th>
                      <th className="px-3 py-2 text-left font-semibold">Links to</th>
                      <th className="px-3 py-2 text-left font-semibold">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, idx) => (
                      <tr key={idx} className={r.errors.length ? "bg-red-50" : "border-t border-zinc-100"}>
                        <td className="px-3 py-2 text-zinc-400">{idx + 2}</td>
                        <td className="px-3 py-2">
                          {r.kind ? (
                            <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold ${KIND_CHIP[r.kind]}`}>
                              {r.kind === "project" ? "Project" : r.kind === "task" ? "Task" : "Subtask"}
                            </span>
                          ) : <span className="italic text-zinc-400">unknown</span>}
                        </td>
                        <td className="px-3 py-2">{r.raw["Title"] || <span className="italic text-zinc-400">blank</span>}</td>
                        <td className="px-3 py-2 text-zinc-500">
                          {r.kind === "project" ? (r.raw["Initiative"] || <span className="italic text-zinc-400">blank</span>)
                            : r.kind === "subtask" ? `${r.raw["Project"] || "?"} › ${r.raw["Parent Task"] || "?"}`
                              : r.kind === "task" ? (r.raw["Project"] || <span className="italic text-zinc-400">blank</span>)
                                : "—"}
                        </td>
                        <td className="px-3 py-2">
                          {r.errors.length === 0 ? (
                            <span className="font-semibold text-emerald-700">✓ Ready</span>
                          ) : (
                            <div>
                              <span className="font-semibold text-red-700">⚠ Fix needed</span>
                              <div className="mt-0.5 text-red-600">{r.errors.join("; ")}</div>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {invalidCount > 0 && (
                <p className="mt-2 text-xs text-zinc-500">
                  Rows that need fixing will be skipped. You can upload the {validRows.length} ready row{validRows.length === 1 ? "" : "s"} now, or fix your file and upload it again.
                </p>
              )}
            </div>
          )}

          {result && (
            <div className="rounded-sm border border-zinc-200 bg-zinc-50 p-4">
              <div className="text-sm font-semibold mb-1">
                {result.created} row{result.created === 1 ? "" : "s"} created
                {result.failed.length > 0 && `, ${result.failed.length} failed`}
              </div>
              {result.failed.length > 0 && (
                <ul className="mt-2 space-y-1 text-xs text-red-700">
                  {result.failed.map((f, i) => (
                    <li key={i}>• [{f.kind}] {f.title || "(untitled)"} — {f.reason || "unknown error"}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          {!result ? (
            <Button
              className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]"
              disabled={validRows.length === 0 || uploading}
              onClick={submit}
              data-testid="bulk-upload-submit"
            >
              {uploading ? "Uploading…" : `Upload ${validRows.length} Row${validRows.length === 1 ? "" : "s"}`}
            </Button>
          ) : (
            <Button className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]" onClick={() => handleClose(false)}>
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
